import {
    CheckCircleFilled,
    ClockCircleFilled,
    ExclamationCircleFilled,
    PauseCircleFilled,
    UserOutlined,
} from '@ant-design/icons'
import Text from 'antd/es/typography/Text'

import { Col, Popover, Row, Spin, theme, Typography } from 'antd'
import { FC, useState } from 'react'
import { DataSourceItem } from '../../../../shipments-page'
import Task, { TaskTypes } from '../../../../../../types/api/task'
import StampInfo from '../stamp-info'
import MaterialInfo from '../material-info'
import FormsInfo from '../forms-info'
import { useLazyGetShipmentByDocIdQuery } from '../../../../../../store/api/shipments-api'
import { shipmentStatusQueryParams } from '../../../../constants/shipment-status-query'
import {
    getApprovalTaskLabel,
    getApprovalTaskTone,
    pickAssignmentApprovalTask,
} from '../../../../../../helpers/entities/shipment/curator-approval'

interface PreparationProps {
    data: DataSourceItem
}

const Preparation: FC<PreparationProps> = ({ data }) => {
    const { token } = theme.useToken()
    const [open, setOpen] = useState(false)
    const [getShipmentByDocId, { data: detailResponse, isFetching }] =
        useLazyGetShipmentByDocIdQuery()

    const detail = detailResponse?.data
    const tasks = detail?.tasks || []
    const formOrders = detail?.form_orders
    const getTasks = (type: TaskTypes) => tasks.filter(task => task.type === type)
    const drawingBlankTasks = getTasks('Отрисовка бланка')
    const drawingStampTasks = getTasks('Отрисовка штампа')
    const formOrderTasks = getTasks('Заказ форм')
    const formReorderTasks = getTasks('Перезаказ форм')
    const cuttingMaterialTasks = getTasks('Разрез материала')
    const digitalDismountingTasks = getTasks('Цифровой спуск')
    const honestSignTasks = getTasks('Честный знак')
    const assignmentApprovalTask = pickAssignmentApprovalTask(tasks)
    const hasBlankPreviewFiles = Boolean(detail?.blankPreviewFiles?.length)
    const hasDrawingBlankTask = drawingBlankTasks.length > 0
    

    const handleOpenChange = async (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen || !data.documentId) return

        // preferCacheValue=false — каждый раз актуальные данные статуса
        await getShipmentByDocId([data.documentId, shipmentStatusQueryParams], false)
    }

    const getIconByTask = (task: Task) => {
        if (task.type === 'Согласование задания') {
            switch (getApprovalTaskTone(task, detail)) {
                case 'approved':
                    return <CheckCircleFilled style={{ color: token.green5 }} />
                case 'rejected':
                    return <ExclamationCircleFilled style={{ color: token.red5 }} />
                case 'waiting':
                    return <PauseCircleFilled style={{ color: token.yellow5 }} />
                case 'closed':
                    return <PauseCircleFilled style={{ color: token.colorTextQuaternary }} />
            }
        }

        switch (task.taskStatus) {
            case 'Новая':
                return <PauseCircleFilled style={{ color: token.yellow5 }} />
            case 'В работе':
                return <ClockCircleFilled style={{ color: token.blue5 }} />
            case 'Завершена':
                return <CheckCircleFilled style={{ color: token.green5 }} />
        }
    }

    const getTaskStatusLabel = (task: Task) => {
        if (task.type === 'Согласование задания') {
            return getApprovalTaskLabel(task, detail)
        }
        return task.taskStatus
    }

    const filteredTasks = [
        ...(assignmentApprovalTask ? [assignmentApprovalTask] : []),
        ...drawingBlankTasks,
        ...drawingStampTasks,
        ...formOrderTasks,
        ...formReorderTasks,
        ...cuttingMaterialTasks,
        ...digitalDismountingTasks,
        ...honestSignTasks,
    ]

    return (
        <Popover
            placement={'rightTop'}
            destroyTooltipOnHide={true}
            open={open}
            onOpenChange={handleOpenChange}
            content={
                isFetching && !detail ? (
                    <Spin size='small' />
                ) : (
                    <>
                        <Typography.Text strong>Задачи</Typography.Text>
                        {!hasDrawingBlankTask && hasBlankPreviewFiles  && (
                            <Row gutter={12}>
                                <Col style={{ width: 120 }}>Бланк</Col>
                                <Col>
                                    <CheckCircleFilled style={{ color: token.green5 }} /> Готов
                                </Col>
                            </Row>
                        )}
                        {filteredTasks.length === 0 && !hasBlankPreviewFiles && (
                            <div>
                                <Text>Нет связанных задач</Text>
                            </div>
                        )}
                        {filteredTasks.map(task => {
                            return (
                                <Row gutter={12} key={task.documentId}>
                                    <Col style={{ width: 120 }}>{task.type}</Col>
                                    <Col>
                                        {getIconByTask(task)} {getTaskStatusLabel(task)}
                                    </Col>
                                    {task.performer && (
                                        <Col>
                                            <UserOutlined /> {task.performer.username}
                                        </Col>
                                    )}
                                </Row>
                            )
                        })}

                        <Typography.Text strong>Заказы форм</Typography.Text>
                        <FormsInfo formOrders={formOrders} />

                        <Typography.Text strong>Оборудование</Typography.Text>
                        <StampInfo stamp={detail?.stamp} />

                        {detail ? <MaterialInfo data={detail} /> : null}
                    </>
                )
            }
        >
            <ExclamationCircleFilled style={{ color: token.orange5, fontSize: '16px' }} />
        </Popover>
    )
}

export default Preparation
